import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Chip,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { AccountTree, CheckCircle } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  intro: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
  },
  cardSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  cardInner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  name: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
  },
}));

const FlowTemplateModal = ({ open, onClose, onCreated }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");

  const cargarPlantillas = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/flowbuilder-templates");
      const lista = data?.templates || [];
      setTemplates(lista);
      // Preseleccionar la primera para que crear sea un solo clic
      if (lista.length > 0) {
        setSelected(lista[0].key);
        setName(lista[0].name);
      }
    } catch (err) {
      toast.error("No se pudieron cargar las plantillas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) cargarPlantillas();
  }, [open, cargarPlantillas]);

  const handleSelect = (tpl) => {
    setSelected(tpl.key);
    setName(tpl.name);
  };

  const handleCrear = async () => {
    if (!selected) return;
    setCreating(true);
    try {
      const { data } = await api.post("/flowbuilder-templates", {
        templateKey: selected,
        name: name.trim() || undefined,
      });
      toast.success(`Flujo "${data.name}" creado. Ábrelo para editarlo.`);
      if (onCreated) onCreated(data);
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "No se pudo crear el flujo desde la plantilla"
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear flujo desde una plantilla</DialogTitle>

      <DialogContent dividers>
        <Typography className={classes.intro}>
          Las plantillas se crean con sus nodos ya conectados y listos para
          funcionar. Puedes probarlas tal cual y luego editarlas en el editor
          visual.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {templates.map((tpl) => (
              <Card
                key={tpl.key}
                className={`${classes.card} ${
                  selected === tpl.key ? classes.cardSelected : ""
                }`}
                elevation={0}
              >
                <CardActionArea onClick={() => handleSelect(tpl)}>
                  <div className={classes.cardInner}>
                    {selected === tpl.key ? (
                      <CheckCircle color="primary" />
                    ) : (
                      <AccountTree color="disabled" />
                    )}
                    <Box flex={1}>
                      <Typography className={classes.name}>
                        {tpl.name}
                        <Chip
                          size="small"
                          label={`${tpl.nodeCount} pasos`}
                          variant="outlined"
                        />
                      </Typography>
                      <Typography className={classes.description}>
                        {tpl.description}
                      </Typography>
                    </Box>
                  </div>
                </CardActionArea>
              </Card>
            ))}

            {selected && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Nombre del nuevo flujo
                </Typography>
                <TextField
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Menú de bienvenida"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          Cancelar
        </Button>
        <Button
          onClick={handleCrear}
          color="primary"
          variant="contained"
          disabled={!selected || creating || loading}
          startIcon={
            creating ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Crear flujo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowTemplateModal;
